import { isEmpty, cloneDeep } from "lodash";
// import { v4 as uuidv4 } from "uuid";

import types from "./types";

import { foundationFactory, storeyFactory } from "../Enclosure/Takeoff/TakeoffData/TakeoffFactories";
import IDGenerator from "../Enclosure/Takeoff/TakeoffData/IDGenerator";
import {
    foundationTranslator,
    storeyTranslator,
    translateGarageWalls,
} from "../Enclosure/Takeoff/components/utils/componentTranslator";

import { updateObject, capitalize } from "utils";
import cleanComponents from "utils/cleanComponents";
import { calcEla } from "utils/airtightness";
import { applyImportedRoomMigrationIfNeeded } from "utils/roomByRoom";

export const initialState = {
    modelId: null,
    modelData: {},
    modelDetails: {
        simulationStatus: "IDLE",
    },
    modelTakeoff: {
        errors: {},
    },
    modelFiles: [],
    fileUpload: {},
    loading: false,
    error: null,
    modelNotFound: false,
    newestComponents: [],
    recentDuplicates: [],
    review: {},
    checklist: {},
    contentOpen: false,
    dataEntryMode: "Hot2000",
    dataEntryModeToCompare: "Hot2000",
    isDataChanged: false,
    buildingSurfaceArea: {
        foundationSurfaceArea: {
            value: 0,
        },
        aboveGradeStoreysSurfaceArea: {
            value: 0,
        },
    },
    saveTimeout: null,
    modelDataToCompare: {},
    modelDetailsToCompare: {},
    modelTakeoffToCompare: {},
    linkedBaselineToCompare: {},
    linkedBaseline: {},
    lastSnapshot: {},
    energyPlusResults: {},
    isSnapshotUpdating: false,
    isSharingModel: false,
    /** Hourly / binned Green Button series — not part of modelData (avoids dirty Save); not persisted in Firestore. */
    greenButtonData: {
        hourlyGreenButtonData: [],
        combinedUtilityHeatPumpData: [],
    },
    /** Transient GB UI state — not in modelData (avoids save/compare noise). */
    greenButtonDataLoading: false,
    greenButtonUploadLoading: false,
    greenButtonFetchEpoch: 0,
    greenButtonFetchError: null,
    greenButtonWeatherAnalysisLoading: false,
    greenButtonBaseLoadCalculationLoading: false,
};

export default (state = initialState, action) => {
    switch (action.type) {
        case types.MODEL_LOADING:
            return startModelLoading(state, action);
        case types.MODEL_LOADING_END:
            return endModelLoading(state, action);
        case types.SET_MODEL_ERROR:
            return setModelError(state, action);
        case types.SET_MISSING_CODES:
            return setMissingCodes(state, action);
        case types.SET_EXPORT_ERROR:
            return setExportError(state, action);
        case types.SET_MODEL_DATA:
            return modelInit(state, action);
        case types.SET_MODEL_INFO:
            return setPreRetrofitModelData(state, action);
        case types.SET_MISSING_RESULTS:
            return setMissingResults(state, action);
        case types.CLEAR_MODEL_DATA:
            return clearModelData(state, action);
        case types.SET_MODEL_CODE:
            return setModelCode(state, action);
        case types.SET_MODEL_COMPONENT:
            return setModelComponent(state, action);
        case types.CLEAR_NEW_COMPONENTS:
            return clearNewestComponents(state, action);
        case types.SET_MODEL_FILES:
            return setModelFiles(state, action);
        case types.SET_FILE_UPLOAD_STATUS:
            return setFileUploadStatus(state, action);
        case types.UPDATE_CHECKLIST:
            return updateChecklistItem(state, action);
        case types.UPDATE_MODEL_DETAILS:
            return updateModelDetails(state, action);
        case types.TOGGLE_CONTENT_OPEN:
            return toggleContenOpen(state, action);
        case types.MOVE_MODEL_START:
            return {
                ...state,
                loading: true,
            };
        case types.MOVE_MODEL_SUCCESS:
            return {
                ...state,
                loading: false,
            };
        case types.DUPLICATE_MODEL_START:
            return {
                ...state,
                loading: true,
            };
        case types.DUPLICATE_MODEL_SUCCESS:
            return {
                ...state,
                loading: false,
            };
        case types.ADD_MODEL_TAG_START:
            return addModelTag(state, action);
        case types.REMOVE_MODEL_TAG_START:
            return removeModelTag(state, action);
        case types.SHARE_FILE_ORG_ERROR:
        case types.SHARE_FILE_ORG_START:
            return {
                ...state,
                isSharingModel: true,
            };
        case types.SHARE_FILE_ORG_SUCCESS:
            return shareModelFile(state, action);
        case types.TOGGLE_SHARE_FILE_ORG_LOADING:
            return {
                ...state,
                isSharingModel: false,
            };
        case types.FLAG_MODEL_ERROR_START:
            return flagModelError(state, action);

        case types.ADD_MODEL_CHBA_COMMUNITY:
            return addModelToChbaCommunity(state, action);
        case types.CHANGE_DATAENTRY_MODE:
            return changeDataentryMode(state, action);
        case types.ADD_TAKEOFF_COMP:
            return addTakeoffComp(state, action);
        case types.REMOVE_TAKEOFF_COMP:
            return removeTakeoffComp(state, action);
        case types.UPDATE_TAKEOFF_COMPONENT:
            return updateTakeoffComponent(state, action);
        case types.TRANSLATE_COMPONENT:
            return translateComponent(state, action);
        case types.SET_TAKEOFF_ERRORS:
            return setTakeoffErrors(state, action);
        case types.REMOVE_TAKEOFF_SUBCOMP:
            return removeTakeoffSubcomponent(state, action);
        case types.ADD_TAKEOFF_SUBCOMP:
            return addTakeoffSubcomponent(state, action);
        case types.EDIT_TAKEOFF_SUBCOMP:
            return editTakeoffSubcomponent(state, action);
        case types.DUPLICATE_TAKEOFF_SUBCOMP:
            return duplicateTakeoffSubcomponent(state, action);
        case types.ADD_TOFF_TABLE_COMP:
            return addToffTableComp(state, action);
        case types.ADD_TOFF_TABLE_MULTI_ROW:
            return addToffTableMultiRow(state, action);
        case types.EDIT_TOFF_TABLE_COMP:
            return editToffTableComp(state, action);
        case types.REMOVE_TOFF_TABLE_COMP:
            return removeToffTableComp(state, action);
        case types.COUNT_BUILDING_SURFACE_AREA:
            return countBuildingSurfaceArea(state, action);
        case types.SET_DIMENSIONS:
            return setDimensions(state, action);
        case types.UPDATE_SPECIFICATIONS:
            return updateSpecifications(state, action);
        case types.SET_SAVE_TIMEOUT:
            return setSaveTimeout(state, action);
        case types.ROTATE_WINDOWS:
            return rotateHome(state, action);
        case types.UPDATE_MODEL_SNAPSHOT_START: {
            return {
                ...state,
                isSnapshotUpdating: true,
            };
        }
        case types.UPDATE_MODEL_SNAPSHOT_SUCCESS:
            return {
                ...state,
                isSnapshotUpdating: false,
            };
        case types.UPDATE_MODEL_SNAPSHOT: {
            const snapshot = { ...action.payload };

            if (snapshot.modelData) {
                snapshot.modelData = cloneDeep(snapshot.modelData);
                applyImportedRoomMigrationIfNeeded(snapshot.modelData);
            }

            return {
                ...state,
                lastSnapshot: snapshot,
            };
        }
        case types.UPDATE_NZ_STUDY_REPORT_PDF_DATA:
            return updateNzStudyPdfData(state, action);
        case types.UPDATE_NZ_ROADMAP_PDF_DATA:
            return updateNzRoadmapPdfData(state, action);
        case types.SET_GREEN_BUTTON_DATA:
            return setGreenButtonData(state, action);
        case types.CLEAR_GREEN_BUTTON_DATA:
            return clearGreenButtonStateFromModel(state);
        case types.CLEAR_ORPHANED_GREEN_BUTTON_DATA:
            return clearOrphanedGreenButtonData(state, action);
        case types.GET_GREEN_BUTTON_DATA_FAIL:
            return getModelGreenButtonDataFail(state, action);
        case types.CLEAR_GREEN_BUTTON_FETCH_ERROR:
            return updateObject(state, { greenButtonFetchError: null });
        case types.TOGGLE_GREEN_BUTTON_DATA_LOADING:
            return toggleGreenButtonDataLoading(state, action);
        case types.TOGGLE_GREEN_BUTTON_UPLOAD_LOADING:
            return toggleGreenButtonUploadLoading(state, action);
        case types.BEGIN_GREEN_BUTTON_UPLOAD:
            return beginGreenButtonUpload(state);
        case types.TOGGLE_GREEN_BUTTON_WEATHER_ANALYSIS_LOADING:
            return toggleGreenButtonWeatherAnalysisLoading(state, action);
        case types.TOGGLE_GREEN_BUTTON_BASE_LOAD_CALCULATION_LOADING:
            return toggleGreenButtonBaseLoadCalculationLoading(state, action);
        case types.CHANGE_UTILITY_DATA_PACKAGE:
            return changeUtilityDataPackage(state, action);
        case types.CHANGE_SIMULATION_STATUS:
            return {
                ...state,
                modelDetails: {
                    ...state.modelDetails,
                    simulationStatus: action.payload,
                },
                modelDetailsToCompare: {
                    ...state.modelDetailsToCompare,
                    simulationStatus: action.payload,
                },
            };
        case types.SET_MODEL_NOT_FOUND:
            return {
                ...state,
                modelNotFound: true,
            };
        case types.RUN_ENERGY_PLUS_SUCCESS:
            return updateObject(state, {
                energyPlusResults: action.payload,
            });
        case types.ACTIVATE_INSIGHTS_START:
            return state;
        case types.ACTIVATE_INSIGHTS_SUCCESS:
            return updateObject(state, {
                modelDetails: updateObject(state.modelDetails, {
                    isInsightsActive: true,
                    isRxRResultsUnlocked: true,
                }),
                modelDetailsToCompare: updateObject(state.modelDetailsToCompare, {
                    isInsightsActive: true,
                    isRxRResultsUnlocked: true,
                }),
            });
        case types.ACTIVATE_INSIGHTS_ERROR:
            return updateObject(state, {
                modelDetails: updateObject(state.modelDetails, {
                    isInsightsActive: false,
                    isRxRResultsUnlocked: false,
                }),
                modelDetailsToCompare: updateObject(state.modelDetailsToCompare, {
                    isInsightsActive: false,
                    isRxRResultsUnlocked: false,
                }),
            });
        case types.UNLOCK_RXR_RESULTS_START:
            return state;
        case types.UNLOCK_RXR_RESULTS_SUCCESS:
            return updateObject(state, {
                modelDetails: updateObject(state.modelDetails, {
                    isRxRResultsUnlocked: action.payload,
                }),
                modelDetailsToCompare: updateObject(state.modelDetailsToCompare, {
                    isRxRResultsUnlocked: action.payload,
                }),
            });
        case types.UNLOCK_RXR_RESULTS_ERROR:
            return state;
        default:
            return state;
    }
};

const getModelGreenButtonDataFail = (state, action) => {
    const msg =
        typeof action.error === "string" ? action.error : (action.error?.message ?? "Error loading green button data");
    return updateObject(state, {
        error: action.error,
        greenButtonFetchError: msg,
    });
};

const toggleGreenButtonDataLoading = (state, action) =>
    updateObject(state, {
        greenButtonDataLoading: action.isLoading,
    });

const toggleGreenButtonUploadLoading = (state, action) =>
    updateObject(state, {
        greenButtonUploadLoading: action.isLoading,
    });

/** Clears session hourly data and weather flags when a new XML upload starts. */
const beginGreenButtonUpload = (state) => {
    const ud = state.modelData.utilityData || {};
    const prevGb = ud.greenButtonData || {};
    return updateObject(state, {
        greenButtonUploadLoading: true,
        greenButtonFetchError: null,
        greenButtonFetchEpoch: (state.greenButtonFetchEpoch || 0) + 1,
        greenButtonData: {
            hourlyGreenButtonData: [],
            combinedUtilityHeatPumpData: [],
        },
        modelData: updateObject(state.modelData, {
            utilityData: updateObject(ud, {
                greenButtonData: updateObject(prevGb, {
                    hasTemperatureData: false,
                    stationCounts: {},
                    greenButtonDocumentIds: [],
                }),
            }),
        }),
    });
};

const toggleGreenButtonWeatherAnalysisLoading = (state, action) =>
    updateObject(state, {
        greenButtonWeatherAnalysisLoading: action.isLoading,
    });

const toggleGreenButtonBaseLoadCalculationLoading = (state, action) =>
    updateObject(state, {
        greenButtonBaseLoadCalculationLoading: action.isLoading,
    });

/** True if any hourly row has a finite temperature (post–weather-merge). */
const gbHourlyHasFiniteTemperature = (hourly) =>
    Array.isArray(hourly) && hourly.some((row) => row?.temp != null && Number.isFinite(Number(row.temp)));

const setGreenButtonData = (state, action) => {
    const prevPersisted = state.modelData.utilityData?.greenButtonData || {};
    const prevSession = state.greenButtonData || {};
    const incoming = action.data || {};
    const documentIds =
        incoming.greenButtonDocumentIds ?? incoming.documentIds ?? prevPersisted.greenButtonDocumentIds ?? [];

    const mergedHourly = incoming.hourlyGreenButtonData ?? prevSession.hourlyGreenButtonData ?? [];
    const hasTemperatureData =
        typeof incoming.hasTemperatureData === "boolean"
            ? incoming.hasTemperatureData
            : gbHourlyHasFiniteTemperature(mergedHourly);

    const persistedGreenButton = {
        ...prevPersisted,
        fuelType: incoming.fuelType ?? prevPersisted.fuelType ?? "",
        dataSource: incoming.dataSource ?? prevPersisted.dataSource ?? "",
        sourceFileName: incoming.sourceFileName ?? prevPersisted.sourceFileName ?? "",
        greenButtonDocumentIds: [...(Array.isArray(documentIds) ? documentIds : [])],
        hasTemperatureData,
    };

    const sessionGreenButton = {
        ...prevSession,
        hourlyGreenButtonData: incoming.hourlyGreenButtonData ?? prevSession.hourlyGreenButtonData ?? [],
        combinedUtilityHeatPumpData:
            incoming.combinedUtilityHeatPumpBinData ??
            incoming.combinedUtilityHeatPumpData ??
            prevSession.combinedUtilityHeatPumpData ??
            [],
    };

    return updateObject(state, {
        modelData: updateObject(state.modelData, {
            utilityData: updateObject(state.modelData.utilityData || {}, {
                greenButtonData: persistedGreenButton,
            }),
        }),
        greenButtonData: sessionGreenButton,
        greenButtonFetchError: null,
    });
};

const clearGreenButtonStateFromModel = (state) => {
    const ud = state.modelData.utilityData || {};
    return updateObject(state, {
        greenButtonData: {
            hourlyGreenButtonData: [],
            combinedUtilityHeatPumpData: [],
        },
        greenButtonDataLoading: false,
        greenButtonUploadLoading: false,
        greenButtonFetchError: null,
        greenButtonWeatherAnalysisLoading: false,
        greenButtonBaseLoadCalculationLoading: false,
        modelData: updateObject(state.modelData, {
            utilityData: updateObject(ud, {
                greenButtonData: {},
                hasBaseLoadCalculation: false,
            }),
        }),
    });
};

/**
 * Fail-safe for models whose modelData references Green Button data that no longer exists in the
 * model's utilityData subcollection (duplicated models copy the metadata but not the subcollection).
 * Dropping the stale references stops the hydrate retry loop and re-enables the XML upload input.
 */
const clearOrphanedGreenButtonData = (state, action) =>
    updateObject(clearGreenButtonStateFromModel(state), {
        greenButtonFetchError: action.message || null,
    });

const changeUtilityDataPackage = (state, action) => {
    return updateObject(state, {
        modelData: updateObject(state.modelData, {
            utilityData: updateObject(state.modelData.utilityData || {}, {
                selectedPackage: action.pkg,
            }),
        }),
    });
};

const updateChecklistItem = (state, action) => {
    const checklist = state.checklist || {};
    const { list = {} } = checklist;
    const { [action.id]: checklistItem = {} } = list;
    return updateObject(state, {
        checklist: updateObject(checklist, {
            list: updateObject(list, {
                [action.id]: updateObject(checklistItem, action.updates),
            }),
        }),
    });
};

const setFileUploadStatus = (state, action) => {
    return updateObject(state, {
        fileUpload: updateObject(state.fileUpload, action.statusUpdates),
    });
};

const startModelLoading = (state, action) => updateObject(state, { loading: true });

const endModelLoading = (state, action) => updateObject(state, { loading: false, error: false });

const setModelError = (state, action) => updateObject(state, { loading: false, error: action.error });

const setMissingCodes = (state, action) => updateObject(state, { missingCodes: action?.missingCodes || {} });

const clearModelData = (state, action) => updateObject(state, initialState);

const addModelToChbaCommunity = (state, action) => updateObject(state, { communityUpdate: true });

const setModelCode = (state, action) => {
    const { modelData = {} } = state;
    const { codes = {} } = modelData;

    return updateObject(state, {
        modelData: updateObject(modelData, {
            codes: updateObject(codes, {
                [action.codeRef]: action.code,
            }),
        }),
    });
};

const setModelComponent = (state, action) => {
    const { recentDuplicates, newestComponents } = state;
    const componentForDuplicate = action.isDuplicate ? [action.componentId] : [];

    return updateObject(state, {
        newestComponents: [...newestComponents, action.componentId],
        recentDuplicates: [...recentDuplicates, ...componentForDuplicate],
    });
};

const setExportError = (state, action) => {
    const { review } = state;
    return updateObject(state, {
        review: updateObject(review, {
            exportErrors: action.errors,
            location: action.location || "",
            proceedFunc: action.proceedFunc || null,
        }),
    });
};

const clearNewestComponents = (state, action) => {
    const { newestComponents = {} } = state;
    return updateObject(state, {
        newestComponents: [...newestComponents.pop(action.id)],
    });
};

const setModelFiles = (state, action) => {
    return updateObject(state, {
        modelFiles: action.files,
    });
};

const updateModelDetails = (state, action) => {
    return updateObject(state, {
        modelDetails: updateObject(state.modelDetails, action.modelDetails),
    });
};

const setPreRetrofitModelData = (state, action) => {
    return updateObject(state, {
        ...state,
        linkedBaseline: {
            ...state.linkedBaseline,
            selectedModelId: action.modelId || "",
            data: action.model || {},
        },
    });
};

const setMissingResults = (state, action) => {
    return updateObject(state, {
        ...state,
        linkedBaseline: {
            ...state.linkedBaseline,
            hasResults: action.hasResults,
        },
    });
};

// linkedBaseline needs to be passed here
// the data to compare is what checks for changed data (enable save button)
const modelInit = (state, action) => {
    const normalizedModelData = cloneDeep(action.model.modelData || {});

    applyImportedRoomMigrationIfNeeded(normalizedModelData);

    const sameModelRefresh = action.modelId != null && action.modelId === state.modelId;

    // Reconcile stuck simulationStatus when isSimulationRunning is false
    const modelDetails = action.model.modelDetails || {};
    if (modelDetails.simulationStatus === "RUNNING" && !modelDetails.isSimulationRunning) {
        modelDetails.simulationStatus = "IDLE";
    }
    const nextGreenButtonData = sameModelRefresh
        ? state.greenButtonData || { hourlyGreenButtonData: [], combinedUtilityHeatPumpData: [] }
        : { hourlyGreenButtonData: [], combinedUtilityHeatPumpData: [] };
    const nextGreenButtonDataLoading = false;
    const nextGreenButtonUploadLoading = sameModelRefresh ? (state.greenButtonUploadLoading ?? false) : false;
    const nextGreenButtonFetchEpoch = sameModelRefresh ? (state.greenButtonFetchEpoch ?? 0) : 0;
    const nextGreenButtonFetchError = null;
    const nextGreenButtonWeatherAnalysisLoading = sameModelRefresh
        ? (state.greenButtonWeatherAnalysisLoading ?? false)
        : false;
    const nextGreenButtonBaseLoadCalculationLoading = sameModelRefresh
        ? (state.greenButtonBaseLoadCalculationLoading ?? false)
        : false;

    const uploadInProgress = sameModelRefresh && (state.greenButtonUploadLoading ?? false);
    const modelDataForInit = uploadInProgress
        ? updateObject(normalizedModelData, {
              utilityData: updateObject(normalizedModelData.utilityData || {}, {
                  greenButtonData: cloneDeep(state.modelData?.utilityData?.greenButtonData || {}),
              }),
          })
        : normalizedModelData;

    return action.isSaving
        ? updateObject(state, {
              modelId: action.modelId,
              error: false,
              loading: false,
              modelNotFound: false,
              ...action.model,
              modelData: modelDataForInit,
              modelDataToCompare: cloneDeep(modelDataForInit),
              modelDetailsToCompare: cloneDeep(action.model.modelDetails),
              modelTakeoffToCompare: cloneDeep(action.model.modelTakeoff),
              linkedBaselineToCompare: cloneDeep(action.model.linkedBaseline),
              dataEntryModeToCompare: action.model.dataEntryMode ?? "Hot2000",
              greenButtonData: nextGreenButtonData,
              greenButtonDataLoading: nextGreenButtonDataLoading,
              greenButtonUploadLoading: nextGreenButtonUploadLoading,
              greenButtonFetchEpoch: nextGreenButtonFetchEpoch,
              greenButtonFetchError: nextGreenButtonFetchError,
              greenButtonWeatherAnalysisLoading: nextGreenButtonWeatherAnalysisLoading,
              greenButtonBaseLoadCalculationLoading: nextGreenButtonBaseLoadCalculationLoading,
          })
        : updateObject(state, {
              modelId: action.modelId,
              error: false,
              loading: false,
              modelNotFound: false,
              ...action.model,
              modelData: modelDataForInit,
              modelDataToCompare: cloneDeep(modelDataForInit),
              modelDetailsToCompare: cloneDeep(action.model.modelDetails),
              modelTakeoffToCompare: cloneDeep(action.model.modelTakeoff),
              linkedBaselineToCompare: cloneDeep(action.model.linkedBaseline),
              dataEntryModeToCompare: action.model.dataEntryMode ?? "Hot2000",
              greenButtonData: nextGreenButtonData,
              greenButtonDataLoading: nextGreenButtonDataLoading,
              greenButtonUploadLoading: nextGreenButtonUploadLoading,
              greenButtonFetchEpoch: nextGreenButtonFetchEpoch,
              greenButtonFetchError: nextGreenButtonFetchError,
              greenButtonWeatherAnalysisLoading: nextGreenButtonWeatherAnalysisLoading,
              greenButtonBaseLoadCalculationLoading: nextGreenButtonBaseLoadCalculationLoading,
          });
};

const toggleContenOpen = (state, action) =>
    updateObject(state, {
        contentOpen: !state.contentOpen,
    });

const addModelTag = (state, action) => {
    const { modelDetails: { tags: existingTags = [] } = {} } = state;

    return updateObject(state, {
        modelDetails: updateObject(state.modelDetails, {
            tags: [...existingTags, action.tag],
        }),
    });
};

const removeModelTag = (state, action) => {
    const { modelDetails: { tags: existingTags = [] } = {} } = state;

    const remainingTags = existingTags.filter((el) => el !== action.tag) || [];

    return updateObject(state, {
        modelDetails: updateObject(state.modelDetails, {
            tags: remainingTags,
        }),
    });
};

const shareModelFile = (state, action) => {
    const { payload: { userList = [] } = {} } = action;

    return updateObject(state, {
        modelDetails: updateObject(state.modelDetails, {
            sharedWith: [...state.modelDetails.sharedWith, ...userList],
        }),
        modelDetailsToCompare: updateObject(state.modelDetailsToCompare, {
            sharedWith: [...state.modelDetailsToCompare.sharedWith, ...userList],
        }),
    });

    // return updateObject(state, {
    //     modelDetails: updateObject(state.modelDetails, {
    //         sharedWith: [...state.modelDetails.sharedWith, ...userList],
    //     }),
    // });
};

const flagModelError = (state, action) => {
    return updateObject(state, {
        modelDetails: updateObject(state.modelDetails, { modelErrorSubmitted: true }),
    });
};

const changeDataentryMode = (state, action) => {
    return {
        ...state,
        dataEntryMode: action.mode,
    };
};

const addTakeoffComp = (state, action) => {
    if (action.comp === "foundation") {
        return foundationFactory(state, action);
    } else {
        return storeyFactory(state, action);
    }
};

const updateTakeoffComponent = (state, action) => {
    const { origin, value, unit, component = "foundation" } = action.data;
    return {
        ...state,
        modelTakeoff: {
            ...state.modelTakeoff,
            [component]: {
                ...state.modelTakeoff[component],
                [origin]: {
                    value: !isNaN(parseFloat(value)) ? parseFloat(value) : value,
                    unit: unit,
                },
            },
        },
    };
};

const removeTakeoffComp = (state, action) => {
    return {
        ...state,
    };
};

const setTakeoffErrors = (state, action) => {
    const { origin, error, message } = action.data;

    if (!error) {
        if (state.modelTakeoff.errors[origin]) {
            let newState = { ...state };
            delete state.modelTakeoff.errors[origin];
            return { ...newState };
        } else {
            return {
                ...state,
            };
        }
    } else {
        return {
            ...state,
            modelTakeoff: {
                ...state.modelTakeoff,
                errors: {
                    ...state.modelTakeoff.errors,

                    [origin]: {
                        error: error,
                        message: message,
                    },
                },
            },
        };
    }
};

const removeTakeoffSubcomponent = (state, action) => {
    const { parent, id } = action.data;

    const newArray = state.modelTakeoff[parent].components.filter((item) => item.id !== id);

    return {
        ...state,
        isDataChanged: true,
        modelTakeoff: {
            ...state.modelTakeoff,
            [parent]: {
                ...state.modelTakeoff[parent],
                components: newArray,
            },
        },
    };
};

const addTakeoffSubcomponent = (state, action) => {
    const { parent, component } = action.data;
    const newArray = JSON.parse(JSON.stringify(state.modelTakeoff[parent].components));
    newArray.push(component);

    return {
        ...state,
        isDataChanged: true,
        modelTakeoff: {
            ...state.modelTakeoff,
            [parent]: {
                ...state.modelTakeoff[parent],
                components: newArray,
            },
        },
    };
};

const editTakeoffSubcomponent = (state, action) => {
    const { parent, id, field, value, unit } = action.data;
    const newArray = state.modelTakeoff[parent].components.map((item) => {
        if (item.id !== id) return item;

        return {
            ...item,
            [field]: {
                value,
                unit,
            },
        };
    });

    return {
        ...state,
        isDataChanged: true,
        modelTakeoff: {
            ...state.modelTakeoff,
            [parent]: {
                ...state.modelTakeoff[parent],
                components: newArray,
            },
        },
    };
};

const duplicateTakeoffSubcomponent = (state, action) => {
    const { parent, id } = action.data;

    const newArray = state.modelTakeoff[parent].components.flatMap((item) => {
        if (item.id !== id) return [item];

        const dupItem = cloneDeep(item);
        dupItem.id = IDGenerator("dup");

        return [item, dupItem];
    });

    return {
        ...state,
        isDataChanged: true,
        modelTakeoff: {
            ...state.modelTakeoff,
            [parent]: {
                ...state.modelTakeoff[parent],
                components: newArray,
            },
        },
    };
};

const addToffTableComp = (state, action) => {
    const { parent, component } = action.data;
    const newArray = cloneDeep(state.modelTakeoff[parent].tableComponents);
    newArray.push(component);

    return {
        ...state,
        isDataChanged: true,
        modelTakeoff: {
            ...state.modelTakeoff,
            [parent]: {
                ...state.modelTakeoff[parent],
                tableComponents: newArray,
            },
        },
    };
};

const addToffTableMultiRow = (state, action) => {
    const { parent, component = [], overwrite = false } = action.data;
    const newArray = state?.modelTakeoff?.[parent]?.tableComponents || [];

    return {
        ...state,
        isDataChanged: true,
        modelTakeoff: {
            ...state.modelTakeoff,
            [parent]: {
                ...state.modelTakeoff[parent],
                tableComponents: overwrite ? component : [...newArray, ...component],
            },
        },
    };
};

const editToffTableComp = (state, action) => {
    const { parent, id, field, value, unit } = action.data;

    const newArray = state.modelTakeoff[parent].tableComponents.map((item) => {
        if (item.id !== id) return item;

        return {
            ...item,
            [field]: {
                value,
                unit,
            },
        };
    });

    return {
        ...state,
        isDataChanged: true,
        modelTakeoff: {
            ...state.modelTakeoff,
            [parent]: {
                ...state.modelTakeoff[parent],
                tableComponents: newArray,
            },
        },
    };
};

const removeToffTableComp = (state, action) => {
    const { parent, id } = action.data;

    const newArray = state.modelTakeoff[parent].tableComponents.filter((item) => item.id !== id);

    return {
        ...state,
        isDataChanged: true,
        modelTakeoff: {
            ...state.modelTakeoff,
            [parent]: {
                ...state.modelTakeoff[parent],
                tableComponents: newArray,
            },
        },
    };
};

const countBuildingSurfaceArea = (state, action) => {
    const { modelTakeoff, buildingSurfaceArea } = state;

    let newBuildingArea = buildingSurfaceArea;

    const modelTakeoffKeys = Object.keys(modelTakeoff);

    for (let i = 0; i < modelTakeoffKeys.length; i++) {
        const component = modelTakeoff[modelTakeoffKeys[i]];
        const componentType = component.compType;

        if (
            componentType === "crawlspace" ||
            componentType === "basement" ||
            componentType === "walkout" ||
            componentType === "basementcrawlspace"
        ) {
            let totalPonyWallArea = 0;

            if (component.components.length > 0) {
                // tkoffPonyWallPerimeter * (tkoffPonyWallHeight1 + tkoffPonyWallHeight2) / 2	(for each pony wall)
                const ponyWalls = component.components.filter(({ type }) => type === "ponywall");

                if (ponyWalls.length > 0) {
                    for (let index = 0; index < ponyWalls.length; index++) {
                        const wall = ponyWalls[index];

                        const pwHeight1 = getSubCompValue(wall.fields, "height1");
                        const pwHeight2 = getSubCompValue(wall.fields, "height2");
                        const pwHeightPerimeter = getSubCompValue(wall.fields, "perimeter");

                        totalPonyWallArea += (pwHeightPerimeter * (pwHeight1 + pwHeight2)) / 2;
                    }
                }
            }

            switch (componentType) {
                case "crawlspace":
                case "basement":
                case "walkout":
                    newBuildingArea = {
                        ...newBuildingArea,
                        foundationSurfaceArea: {
                            value: component.totalPerimeter.value * component.agHeight.value,
                            // totalPonyWallArea,
                        },
                    };
                    break;
                case "basementcrawlspace": {
                    // tkoffCrwlspPerimeter * tkoffCrwlspAgWallHeight + tkoffBsmtPerimeter * tkoffBsmtAgWallHeight
                    const tkoffBsmtAgWallSurfArea = component.totalPerimeter.value * component.agHeight.value;

                    const tkoffCrwlspAgWallSurfArea =
                        component.crawltotalPerimeter.value * component.crawlagHeight.value;

                    newBuildingArea = {
                        ...newBuildingArea,
                        foundationSurfaceArea: {
                            value: tkoffBsmtAgWallSurfArea + tkoffCrwlspAgWallSurfArea + totalPonyWallArea,
                        },
                    };

                    break;
                }
                default:
                    newBuildingArea = {
                        ...newBuildingArea,
                    };
            }
        } else if (componentType === "first" || componentType === "second" || componentType === "third") {
            //tkoffAgFloorPlanPerimeter + (additionalWallArea / tkoffAgWallHeight)

            let additionalWallArea = 0;

            if (component.components.length > 0) {
                let cathCeilingsWallArea = 0;
                let slopedCeilingsWallArea = 0;
                let raisedCeilingsWallArea = 0;

                //No attic wall surface area
                const cathCeilings = component.components.filter(({ type }) => type === "cathedralceiling");
                const slopedCeilings = component.components.filter(({ type }) => type === "slopedceiling");
                const raisedCeilings = component.components.filter(({ type }) => type === "raisedceiling");

                if (cathCeilings.length > 0) {
                    for (let index = 0; index < cathCeilings.length; index++) {
                        const ceiling = cathCeilings[index];
                        //tkoffCathCeilWidth * tkoffCathCeilHeight

                        const cathCeilingNumGables = getSubCompValue(ceiling.fields, "numGables");
                        const cathCeilingWidth = getSubCompValue(ceiling.fields, "width");
                        const cathCeilingHeight = getSubCompValue(ceiling.fields, "height");

                        cathCeilingsWallArea += (cathCeilingNumGables / 2) * (cathCeilingWidth * cathCeilingHeight);
                    }
                }

                if (slopedCeilings.length > 0) {
                    for (let index = 0; index < slopedCeilings.length; index++) {
                        const ceiling = slopedCeilings[index];
                        //tkoffSlopedCeilWidth * tkoffSlopedCeilHeight

                        const slopedCeilingWidth = getSubCompValue(ceiling.fields, "width");
                        const slopedCeilingHeight = getSubCompValue(ceiling.fields, "height");

                        slopedCeilingsWallArea += slopedCeilingWidth * slopedCeilingHeight;
                    }
                }

                if (raisedCeilings.length > 0) {
                    for (let index = 0; index < raisedCeilings.length; index++) {
                        const ceiling = raisedCeilings[index];
                        //tkoffRaisedCeilHeight * tkoffRaisedCeilPerimeter
                        const raisedCeilingPerimeter = getSubCompValue(ceiling.fields, "perimeter");
                        const raisedCeilingHeight = getSubCompValue(ceiling.fields, "height");
                        raisedCeilingsWallArea += raisedCeilingHeight * raisedCeilingPerimeter;
                    }
                }

                additionalWallArea = cathCeilingsWallArea + slopedCeilingsWallArea + raisedCeilingsWallArea;
            }

            const tkoffAgEqPerimeter =
                component.floorPlanPerimeter.value + additionalWallArea / component.wallHeight.value;

            //tkoffAgEqPerimeter * tkoffAgWallHeight

            newBuildingArea = {
                ...newBuildingArea,
                aboveGradeStoreysSurfaceArea: {
                    value: tkoffAgEqPerimeter * component.wallHeight.value,
                },
            };
        }
    }

    return { ...state, buildingSurfaceArea: newBuildingArea };
};

const getSubCompValue = (fields, propertyName = "") => {
    const { value = 0 } = fields.find((field) => field.field === propertyName) || {};

    return value;
};

const translateComponent = (state, action) => {
    const { componentType, formModelData: modelData = {} } = action;
    const { modelTakeoff } = state;

    const { components: componentData, defaultCodes = {} } = modelData;

    const cleanedComps = cleanComponents(componentData);

    let newState = {
        ...state,
        modelData: {
            ...modelData,
            components: cleanedComps,
        },
    };

    const component = modelTakeoff[componentType];

    // FOUNDATION LOGIC
    if (componentType === "foundation") {
        const foundationType = component.compType.toLowerCase();
        const garagewalls = translateGarageWalls(component, defaultCodes);

        const baseComponents = {
            ...newState.modelData.components,
            wall: {
                ...newState.modelData.components.wall,
                ...garagewalls,
            },
        };

        switch (foundationType) {
            case "slab":
            case "slab-on-grade": {
                const { slab } = foundationTranslator(component, defaultCodes);
                newState = {
                    ...newState,
                    modelData: {
                        ...newState.modelData,
                        components: {
                            ...baseComponents,
                            slab: {
                                ...newState.modelData.components.slab,
                                takeoffSlab: slab,
                            },
                        },
                    },
                };
                break;
            }

            case "basementcrawlspace": {
                const { basement, crawlspace } = foundationTranslator(component, defaultCodes);
                newState = {
                    ...newState,
                    modelData: {
                        ...newState.modelData,
                        components: {
                            ...baseComponents,
                            basement: {
                                ...newState.modelData.components.basement,
                                takeoffBasementBC: basement,
                            },
                            crawlspace: {
                                ...newState.modelData.components.crawlspace,
                                takeoffCrawlspaceBC: crawlspace,
                            },
                        },
                    },
                };
                break;
            }

            case "walkout": {
                const { basement, slab } = foundationTranslator(component, defaultCodes);
                newState = {
                    ...newState,
                    modelData: {
                        ...newState.modelData,
                        components: {
                            ...baseComponents,
                            basement: {
                                ...newState.modelData.components.basement,
                                takeoffBasementW: basement,
                            },
                            slab: {
                                ...newState.modelData.components.slab,
                                takeoffSlabW: slab,
                            },
                        },
                    },
                };
                break;
            }

            case "crawlspace": {
                const crawl = foundationTranslator(component, defaultCodes);
                newState = {
                    ...newState,
                    modelData: {
                        ...newState.modelData,
                        components: {
                            ...baseComponents,
                            crawlspace: {
                                ...newState.modelData.components.crawlspace,
                                takeoffCrawlspace: crawl,
                            },
                        },
                    },
                };
                break;
            }

            default: {
                const foundation = foundationTranslator(component, defaultCodes);
                newState = {
                    ...newState,
                    modelData: {
                        ...newState.modelData,
                        components: {
                            ...baseComponents,
                            basement: {
                                ...newState.modelData.components.basement,
                                takeoffBasement: foundation,
                            },
                        },
                    },
                };
            }
        }
    } else {
        // STOREY TRANSLATION
        const translatedStorey = storeyTranslator(component, defaultCodes);
        const { takeoffWall, walls, ceilings, expFloors } = translatedStorey;

        newState = {
            ...newState,
            isDataChanged: true,
            modelData: {
                ...newState.modelData,
                components: {
                    ...newState.modelData.components,
                    wall: {
                        ...newState.modelData.components.wall,
                        [`takeoffWall-${component.compType}`]: takeoffWall,
                        ...walls,
                    },
                    ceiling: {
                        ...newState.modelData.components.ceiling,
                        ...ceilings,
                    },
                    expFloor: {
                        ...newState.modelData.components.expFloor,
                        ...expFloors,
                    },
                },
            },
        };
    }

    newState.modelData.components = cleanComponents(newState.modelData.components);

    // ========== DIMENSIONS SETUP ========== //
    const {
        dimensions: {
            aboveGradeIntFloorArea: { items: existingAgItems = {} } = {},
            belowGradeIntFloorArea: { items: existingBgItems = {} } = {},
            volume: { items: existingVolumeItems = {} } = {},
        } = {},
    } = newState.modelData;

    let aboveGradeIntFloorAreaItems = { ...existingAgItems };
    let belowGradeIntFloorAreaItems = { ...existingBgItems };
    let totalHouseVolumeItems = { ...existingVolumeItems };

    let additionalEnclosedVolume = 0;
    let inclCrawlVolume = false;
    let tkoffAgWallHeight = 0;
    let highestStorey = [];

    if (componentType === "foundation") {
        const foundationType = component.compType.toLowerCase();

        const foundationAreaValue = parseFloat(component.area?.value?.toFixed(2) || 0);
        const foundationVolumeValue = parseFloat(component.volume?.value?.toFixed(2) || 0);

        switch (foundationType) {
            case "basement":
            case "walkout":
                totalHouseVolumeItems[componentType] = {
                    enabled: true,
                    value: foundationVolumeValue,
                    description: `${capitalize(foundationType)} volume`,
                };
                belowGradeIntFloorAreaItems[componentType] = {
                    enabled: true,
                    value: foundationAreaValue,
                    description: `${capitalize(foundationType)}`,
                };
                break;

            case "crawlspace":
                inclCrawlVolume = !!component.includeCrawlspaceVolume?.value;
                totalHouseVolumeItems[componentType] = {
                    enabled: true,
                    value: inclCrawlVolume ? foundationVolumeValue : 0,
                    description: "Crawlspace volume",
                };
                belowGradeIntFloorAreaItems[componentType] = {
                    enabled: true,
                    value: inclCrawlVolume ? foundationAreaValue : 0,
                    description: "Crawlspace",
                };
                break;

            case "basementcrawlspace":
                totalHouseVolumeItems[foundationType] = {
                    enabled: true,
                    value: foundationVolumeValue,
                    description: "Basement/Crawl volume",
                };
                belowGradeIntFloorAreaItems = {
                    [`${foundationType.toLowerCase()}`]: {
                        enabled: true,
                        value: foundationAreaValue,
                        description: "Basement",
                    },
                    [`${foundationType.toLowerCase()} - 2`]: {
                        enabled: true,
                        value: parseFloat(component.crawlarea?.value?.toFixed(2) || 0),
                        description: "Crawlspace",
                    },
                };
                break;

            default:
                break;
        }

        newState = {
            ...newState,
            modelData: {
                ...newState.modelData,
                dimensions: {
                    ...newState.modelData.dimensions,
                    belowGradeIntFloorArea: {
                        items: belowGradeIntFloorAreaItems,
                        total: Object.values(belowGradeIntFloorAreaItems).reduce((sum, { value }) => sum + value, 0),
                    },
                },
            },
        };
    } else {
        // Storeys
        tkoffAgWallHeight += modelTakeoff?.foundation?.agHeight?.value || 0;
        tkoffAgWallHeight += modelTakeoff?.foundation?.floorHeaderHeight?.value || 0;
        tkoffAgWallHeight += component.wallHeight?.value || 0;
        tkoffAgWallHeight += component.floorHeaderHeight?.value || 0;

        const { foundation, [componentType]: currentKey, other, ...restKeys } = modelTakeoff;

        Object.keys(restKeys).forEach((key) => {
            const { components: subComps = [], wallHeight = {}, floorHeaderHeight = {} } = modelTakeoff[key];
            tkoffAgWallHeight += wallHeight.value || 0;
            tkoffAgWallHeight += floorHeaderHeight.value || 0;

            subComps.forEach((subComp) => {
                if (["cathedralceiling", "slopedceiling", "raisedceiling"].includes(subComp.type)) {
                    const height = getSubCompValue(subComp.fields, "height");
                    highestStorey.push(height);
                }
            });
        });

        component.components?.forEach((ceiling) => {
            const type = ceiling.type;
            const height = getSubCompValue(ceiling.fields, "height");
            highestStorey.push(height);

            if (type === "cathedralceiling") {
                const w = getSubCompValue(ceiling.fields, "width");
                const l = getSubCompValue(ceiling.fields, "length");
                additionalEnclosedVolume += 0.5 * w * height * l;
            }
            if (type === "slopedceiling") {
                const w = getSubCompValue(ceiling.fields, "width");
                const l = getSubCompValue(ceiling.fields, "length");
                additionalEnclosedVolume += 0.5 * height * w * l;
            }
            if (type === "raisedceiling") {
                const a = getSubCompValue(ceiling.fields, "area");
                additionalEnclosedVolume += a * height;
            }
        });

        aboveGradeIntFloorAreaItems[component.compType] = {
            enabled: true,
            description: `${capitalize(component.compType)} storey area`,
            value: parseFloat(component.floorPlanArea?.value?.toFixed(2) || 0),
        };

        const heightOfHighestCeiling = tkoffAgWallHeight + (highestStorey.length ? Math.max(...highestStorey) : 0);

        totalHouseVolumeItems[componentType] = {
            enabled: true,
            description: `${capitalize(componentType)} storey volume`,
            value: Number(component.volume?.value || 0) + additionalEnclosedVolume,
        };

        newState = {
            ...newState,
            modelData: {
                ...newState.modelData,
                dimensions: {
                    ...newState.modelData.dimensions,
                    aboveGradeIntFloorArea: {
                        items: aboveGradeIntFloorAreaItems,
                        total: Object.values(aboveGradeIntFloorAreaItems).reduce((sum, { value }) => sum + value, 0),
                    },
                    highestCeiling: {
                        ...newState.modelData.dimensions.highestCeiling,
                        total: heightOfHighestCeiling,
                    },
                },
            },
        };
    }

    const newTotalVolume = Object.values(totalHouseVolumeItems).reduce((sum, { value }) => sum + value, 0);
    const { id: airTightnessTypeId = 0 } = newState.modelData.airTightness?.testType || {};

    const ach = newState.modelData.airTightness?.blowerTest?.airChangeRate50Pa || 1;
    const newEla = calcEla(newTotalVolume, ach, airTightnessTypeId);

    newState = {
        ...newState,
        modelData: {
            ...newState.modelData,
            dimensions: {
                ...newState.modelData.dimensions,
                volume: {
                    items: totalHouseVolumeItems,
                    total: newTotalVolume,
                    includeCrawlspaceVolume: inclCrawlVolume,
                },
            },
            airTightness: {
                ...newState.modelData.airTightness,
                blowerTest: {
                    ...newState.modelData.airTightness.blowerTest,
                    eqLeakageArea: newEla,
                },
            },
        },
    };

    return newState;
};

const setDimensions = (state, action) => {
    const { modelTakeoff } = state;

    //NO LONGER USED

    return { ...state };
};

//Fixes bug when you choose amount of storeys/plan shape/bedrooms and it resets
//after user presses "convert takeoff" button
const updateSpecifications = (state, action) => {
    const { specificationType, value, formModelData: modelData = {} } = action.payload;

    switch (action.payload.specificationType) {
        case "numStoreys":
        case "planShape":
            return {
                ...state,
                modelData: {
                    ...modelData,
                    specifications: {
                        ...modelData.specifications,
                        [specificationType]: { id: value },
                    },
                },
            };
        case "minBedroomVentilation":
            return {
                ...state,
                modelData: {
                    ...modelData,
                    ventilation: {
                        ...modelData.ventilation,
                        bedrooms: value,
                        rooms: {
                            ...modelData.ventilation.rooms,
                            bedrooms: value,
                        },
                    },
                },
            };
        default:
            return {
                ...state,
                modelData,
            };
    }
};

const setSaveTimeout = (state, action) => ({
    ...state,
    saveTimeout: action.payload,
});

const rotateHome = (state, action) => {
    const { windows, newHomeDirection, formModelData: modelData = {} } = action.payload;
    const { id } = newHomeDirection;

    let newState = cloneDeep(state);

    newState.modelData.specifications.facingDirection.id = id;

    const windowsObject = windows.reduce((prev, w) => ({ ...prev, [w.componentId]: w }), {});
    const { components = {} } = newState.modelData;
    const componentsKeys = Object.keys(components);

    for (let i = 0; i < componentsKeys.length; i++) {
        const compKey = componentsKeys[i];

        if (!["wall", "ceiling", "basement", "crawlspace"].includes(compKey)) continue;

        const currentComponents = components[compKey];
        if (isEmpty(currentComponents)) continue;

        const currentComponentsKeys = Object.keys(currentComponents);

        for (let j = 0; j < currentComponentsKeys.length; j++) {
            const curCompKey = currentComponentsKeys[j];
            const { subcomponents } = currentComponents[curCompKey];
            if (isEmpty(subcomponents)) continue;

            const { window = {}, door = {} } = subcomponents;

            const doorKeys = Object.keys(door);
            const subWindowsKeys = Object.keys(window);

            for (let dw = 0; dw < doorKeys.length; dw++) {
                const { subcomponents: { window: windowsFromDoor = {} } = {} } = door[doorKeys[dw]];
                const subDoorWindowsKeys = Object.keys(windowsFromDoor);

                for (let w = 0; w < subDoorWindowsKeys.length; w++) {
                    const wKey = subDoorWindowsKeys[w];
                    if (!windowsFromDoor[wKey] || !windowsObject[wKey]) continue;

                    windowsFromDoor[wKey].facingDirection.id = windowsObject[wKey]?.newDirectionId;
                }
            }

            for (let w = 0; w < subWindowsKeys.length; w++) {
                const wKey = subWindowsKeys[w];
                if (!window[wKey] || !windowsObject[wKey]) continue;

                window[wKey].facingDirection.id = windowsObject[wKey]?.newDirectionId;
            }
        }
    }

    return newState;
};

const updateNzStudyPdfData = (state, action) => {
    const { fieldName, value } = action.payload;

    return {
        ...state,
        modelData: {
            ...state.modelData,
            nzStudyReportPdfData: {
                ...state.modelData.nzStudyReportPdfData,
                [fieldName]: value,
            },
        },
    };
};

const updateNzRoadmapPdfData = (state, action) => {
    const { fieldName, value } = action.payload;

    return {
        ...state,
        modelData: {
            ...state.modelData,
            nzRoadmapPdfData: {
                ...state.modelData.nzRoadmapPdfData,
                [fieldName]: value,
            },
        },
    };
};
